import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext.jsx';
import useSSE from '../hooks/useSSE.js';
import * as notificationsService from '../services/notifications.service.js';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const toastTimeoutsRef = useRef(new Set());

  const apiBase = import.meta.env.VITE_API_URL || '/api/v1';

  const resolveSseUrl = useCallback(async () => {
    try {
      const res = await notificationsService.getStreamToken();
      if (!res.success || !res.data?.sseToken) return null;
      return `${apiBase}/notifications/stream?sse_token=${encodeURIComponent(res.data.sseToken)}`;
    } catch (err) {
      console.error('[NotificationContext] Failed to get SSE token:', err);
      return null;
    }
  }, [apiBase]);

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      try {
        const [notifRes, countRes] = await Promise.all([
          notificationsService.getNotifications(1, 20),
          notificationsService.getUnreadCount(),
        ]);
        if (notifRes.success) setNotifications(notifRes.data?.notifications || notifRes.data || []);
        if (countRes.success) setUnreadCount(countRes.data?.count || 0);
      } catch (err) {
        console.error('[NotificationContext] Error loading notifications:', err);
      }
    };
    load();
  }, [isAuthenticated]);

  useEffect(() => () => {
    for (const timeoutId of toastTimeoutsRef.current) {
      clearTimeout(timeoutId);
    }
    toastTimeoutsRef.current.clear();
  }, []);

  // Two-phase dismissal: flag the toast `leaving` (plays the exit animation),
  // then drop it after the animation window (~220ms). Shared by auto-dismiss
  // and manual close so both animate out.
  const scheduleRemoval = useCallback((id) => {
    setToasts((prev) => prev.map((t) => (t.toastId === id ? { ...t, leaving: true } : t)));
    const timeoutId = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.toastId !== id));
      toastTimeoutsRef.current.delete(timeoutId);
    }, 220);
    toastTimeoutsRef.current.add(timeoutId);
  }, []);

  const addToast = useCallback((notification) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { ...notification, toastId: id, leaving: false }]);
    const timeoutId = setTimeout(() => {
      scheduleRemoval(id);
      toastTimeoutsRef.current.delete(timeoutId);
    }, 5000);
    toastTimeoutsRef.current.add(timeoutId);
  }, [scheduleRemoval]);

  const removeToast = useCallback((toastId) => {
    scheduleRemoval(toastId);
  }, [scheduleRemoval]);

  const handleSSEMessage = useCallback((data) => {
    if (data.type !== 'notification' || !data.payload) return;
    const notif = data.payload;

    setNotifications((prev) => [notif, ...prev].slice(0, 50));
    setUnreadCount((prev) => prev + 1);

    // Server only emits an SSE toast when the user's per-category in-app pref is
    // on, so anything that arrives here is meant to be shown.
    addToast(notif);
  }, [addToast]);

  useSSE(resolveSseUrl, { onMessage: handleSSEMessage, enabled: isAuthenticated });

  const markAsRead = useCallback(async (id) => {
    try {
      await notificationsService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('[NotificationContext] markAsRead error:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('[NotificationContext] markAllAsRead error:', err);
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    toasts,
    removeToast,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    return { notifications: [], unreadCount: 0, toasts: [], removeToast: () => {}, markAsRead: () => {}, markAllAsRead: () => {} };
  }
  return context;
}
